import type {FC} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import ImageFill from '@/modules/foundation/components/image/ImageFill';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';

import type {ArticleDetails} from '../../shared/types';

type Props = {
	article: ArticleDetails;
	headingLevel: 2 | 3;
	featured?: boolean;
	hideDescriptionOnMobile?: boolean;
};

export const ArticleCard: FC<Props> = ({
	article,
	headingLevel,
	featured,
	hideDescriptionOnMobile,
}) => {
	const {title, href, imageWeb} = article;

	return (
		<Link href={href} className={clsx('block', 'h-full', 'bg-white', 'group relative')}>
			{imageWeb && (
				<div
					className={clsx(
						'relative',
						'overflow-hidden',
						featured
							? 'aspect-video'
							: hideDescriptionOnMobile
								? ['aspect-[175/140]', 'md:aspect-video']
								: ['aspect-video'],
					)}
				>
					<ImageFill
						height={imageWeb.image.height}
						src={imageWeb.image.src}
						width={imageWeb.image.width}
						alt={imageWeb.altText ?? ''}
						hotspot={imageWeb.image.hotspot}
						className="duration-800 object-cover transition-transform group-hover:scale-125 motion-reduce:group-hover:scale-100"
					/>
				</div>
			)}
			<div className={clsx('p-4', 'md:p-5')}>
				{featured ? (
					<Heading
						className="duration-600 transition-colors group-hover:text-red-600"
						level={headingLevel}
						size="h3"
						spacing
					>
						{title}
					</Heading>
				) : (
					<Heading
						className="duration-600 transition-colors group-hover:text-red-600"
						level={headingLevel}
						size="h5"
						spacing
					>
						{title}
					</Heading>
				)}
				{article.description && (
					<Paragraph className={clsx(hideDescriptionOnMobile && ['hidden', 'md:block'])}>
						{article.description}
					</Paragraph>
				)}
			</div>
		</Link>
	);
};
